import { supabase } from '@/lib/supabaseClient';

/**
 * Gets the organization ID for the current authenticated user with multiple fallback strategies
 * @returns Promise with organization ID or null if not found
 */
export async function getCurrentOrgId(): Promise<string | null> {
  try {
    // Step 1: Get the current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      console.error('Failed to get current user:', userError);
      return null;
    }

    // Step 2: Try to get org_id via RPC call (most efficient)
    try {
      const { data: orgData, error: orgError } = await supabase.rpc('get_user_org_id', {
        p_user_id: user.id
      });
      
      if (!orgError && orgData) {
        return orgData as string;
      }
    } catch (err) {
      console.warn('RPC call failed, trying alternative methods');
    }

    // Step 3: Try to get org_id directly from user_profiles
    const { data: profileData, error: profileError } = await supabase
      .from('user_profiles')
      .select('org_id')
      .eq('user_id', user.id)
      .single();
    
    if (!profileError && profileData && profileData.org_id) {
      return profileData.org_id as string;
    }

    // Step 4: If no profile or no org_id in profile, try to get the first organization
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select('id')
      .limit(1);
      
    if (!orgError && orgData && orgData.length > 0) {
      return orgData[0].id as string;
    }

    // No organization found
    console.warn('No organization found for the current user');
    return null;
  } catch (err) {
    console.error('Error retrieving organization ID:', err);
    return null;
  }
}

/**
 * Gets the organization ID synchronously from user store if available
 * Falls back to localStorage if available
 * @returns Organization ID or null if not found
 */
export function getCurrentOrgIdSync(): string | null {
  try {
    // Try to get from localStorage first (if the app previously stored it there)
    const storedOrgId = localStorage.getItem('current_org_id');
    if (storedOrgId) {
      return storedOrgId;
    }
    
    return null;
  } catch (err) {
    console.error('Error retrieving organization ID synchronously:', err);
    return null;
  }
} 